import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { setLibraryPublic } from "@/lib/db";

interface ShareLibraryModalProps {
  userId: string;
  onClose: () => void;
}

export default function ShareLibraryModal({ userId, onClose }: ShareLibraryModalProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const libraryUrl = typeof window !== "undefined"
    ? `${window.location.origin}/library/${userId}`
    : "";

  useEffect(() => {
    let mounted = true;
    setLibraryPublic(userId)
      .then(() => { if (mounted) setError(null); })
      .catch((e) => {
        console.warn("Could not set library public:", e);
        if (mounted) setError(t("library.shareError"));
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [userId, t]);

  const handleCopy = async () => {
    if (!libraryUrl) return;
    try {
      await navigator.clipboard.writeText(libraryUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setError(t("library.copyError"));
    }
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: 16,
        backdropFilter: "blur(6px)",
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="modal-box"
        style={{
          background: "hsl(var(--bg2))",
          border: "1px solid hsl(var(--border))",
          borderRadius: "var(--radius)",
          padding: 28,
          width: "100%",
          maxWidth: 440,
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: "0 24px 64px rgba(0,0,0,.5)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 12,
                background: "linear-gradient(135deg, rgba(0,229,160,.2), rgba(109,90,255,.2))",
                border: "1px solid rgba(0,229,160,.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 24,
              }}
            >
              📚
            </div>
            <div>
              <div
                style={{
                  fontFamily: "Instrument Serif, serif",
                  fontSize: 20,
                  fontWeight: 600,
                  color: "hsl(var(--foreground))",
                }}
              >
                {t("library.shareTitle")}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "hsl(var(--muted-foreground))",
                  marginTop: 2,
                }}
              >
                {t("library.shareSubtitle")}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "hsl(var(--muted-foreground))",
              cursor: "pointer",
              fontSize: 20,
              padding: 4,
            }}
            aria-label={t("common.close")}
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div style={{ padding: 24, textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
            {t("library.enabling")}
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 20 }}>
              <label
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "hsl(var(--muted-foreground))",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 8,
                  display: "block",
                }}
              >
                {t("library.yourLink")}
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  readOnly
                  value={libraryUrl}
                  style={{
                    flex: 1,
                    padding: "12px 16px",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid hsl(var(--border))",
                    background: "hsl(var(--bg3))",
                    color: "hsl(var(--foreground))",
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 12,
                  }}
                />
                <button
                  onClick={handleCopy}
                  style={{
                    padding: "12px 20px",
                    borderRadius: 50,
                    border: copied
                      ? "1px solid hsl(var(--accent2))"
                      : "1px solid hsl(var(--border))",
                    background: copied ? "rgba(0,229,160,.15)" : "hsl(var(--primary))",
                    color: copied ? "hsl(var(--accent2))" : "white",
                    fontFamily: "DM Sans, sans-serif",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "all .2s",
                  }}
                >
                  {copied ? t("library.copied") : t("library.copy")}
                </button>
              </div>
            </div>

            {error && (
              <div
                style={{
                  padding: "10px 14px",
                  borderRadius: "var(--radius-sm)",
                  background: "rgba(255,90,138,.1)",
                  border: "1px solid rgba(255,90,138,.3)",
                  color: "hsl(var(--accent3))",
                  fontSize: 13,
                  marginBottom: 16,
                }}
              >
                {error}
              </div>
            )}

            <p
              style={{
                fontSize: 12,
                color: "hsl(var(--muted-foreground))",
                lineHeight: 1.5,
                marginBottom: 20,
              }}
            >
              {t("library.shareHint")}
            </p>
          </>
        )}

        <button
          onClick={onClose}
          style={{
            width: "100%",
            padding: "10px 20px",
            borderRadius: 50,
            border: "1px solid hsl(var(--border))",
            background: "transparent",
            color: "hsl(var(--muted-foreground))",
            fontFamily: "DM Sans, sans-serif",
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "hsl(var(--primary))";
            e.currentTarget.style.color = "hsl(var(--foreground))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "hsl(var(--border))";
            e.currentTarget.style.color = "hsl(var(--muted-foreground))";
          }}
        >
          {t("library.close")}
        </button>
      </div>
    </div>
  );
}
